import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../App";
import "../components/stockread/sr.css";
import { API } from "../components/stockread/srUtil";
import { SrSearch } from "../components/stockread/SrSearch";
import { SrTabs } from "../components/stockread/SrTabs";
import { StockHeader, SrFooter } from "../components/stockread/StockHeader";
import { CurrentSetup } from "../components/stockread/CurrentSetup";
import { LongTerm } from "../components/stockread/LongTerm";
import { TradeClock } from "../components/stockread/TradeClock";
import { RegimeNowcast } from "../components/stockread/RegimeNowcast";
import { Earnings } from "../components/stockread/Earnings";
import { SrDialog } from "../components/stockread/SrDialog";

const Status = ({ children, testid }) => <div className="card pad muted" style={{ marginTop: 10 }} data-testid={testid}>{children}</div>;

const Screens = ({ d, tab, onDetail, onRead }) => (
  <>
    <div id="screen-current" role="tabpanel" aria-labelledby="tab-current" hidden={tab !== "current"}>
      {tab === "current" && <CurrentSetup d={d} onDetail={onDetail} />}
    </div>
    <div id="screen-long" role="tabpanel" aria-labelledby="tab-long" hidden={tab !== "long"}>
      {tab === "long" && <LongTerm d={d} onDetail={onDetail} onRead={onRead} />}
    </div>
    <div id="screen-short" role="tabpanel" aria-labelledby="tab-short" hidden={tab !== "short"}>
      {tab === "short" && (
        <>
          <section className="sectionhead"><span className="eyebrow">Short-term outlook</span><h2>How is it going so far?</h2></section>
          <TradeClock d={d} />
          <RegimeNowcast d={d} onDetail={onDetail} />
        </>
      )}
    </div>
    <div id="screen-earnings" role="tabpanel" aria-labelledby="tab-earnings" hidden={tab !== "earnings"}>
      {tab === "earnings" && (
        <>
          <Earnings e={d.short.earnings} symbol={d.symbol} onDetail={onDetail} />
        </>
      )}
    </div>
  </>
);

export default function StockReadPage() {
  const { symbol } = useParams();
  const nav = useNavigate();
  const { token } = useAuth();
  const [universe, setUniverse] = useState(null);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [tab, setTab] = useState("current");
  const [detail, setDetail] = useState(null);
  const [follows, setFollows] = useState({});
  const sym = (symbol || "").toUpperCase();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetch(`${API}/stock-read/symbols`, auth).then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setUniverse).catch(() => setStatus("error"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!universe) return;
    if (!sym) { if (universe.symbols?.length) nav(`/stock-read/${universe.symbols[0].symbol}`, { replace: true }); return; }
    if (!universe.symbols.some((s) => s.symbol === sym)) { setStatus("missing"); return; }
    let live = true;
    setStatus("loading"); setDetail(null);
    fetch(`${API}/stock-read/${sym}`, auth)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (!live) return; setData(d); setStatus(d.available ? "ready" : "missing"); })
      .catch(() => live && setStatus("error"));
    return () => { live = false; };
  }, [universe, sym]); // eslint-disable-line react-hooks/exhaustive-deps

  const read = (s) => { setDetail(null); nav(`/stock-read/${s}`); };
  const d = status === "ready" && data?.symbol === sym ? data : null;
  return (
    <div className="sr -m-4 lg:-m-6" style={{ minHeight: "100vh" }} data-testid="stock-read-page">
      <header><div className="nav row">
        <span className="brand">fundprints<em>•</em></span>
        <span className="concept" data-testid="sr-concept-tag">LIVE DATA</span>
        {universe && <SrSearch symbols={universe.symbols} onGo={read} />}
      </div></header>
      <main className="wrap">
        <div className="breadcrumb">Research &nbsp; / &nbsp; Stock Read &nbsp; / &nbsp; <span style={{ color: "var(--text)" }}>{sym || "—"}</span></div>
        {status === "loading" && <Status testid="sr-loading">Loading {sym || "stock"}…</Status>}
        {status === "error" && <Status testid="sr-error">Could not load this stock. Try again shortly.</Status>}
        {status === "missing" && <Status testid="sr-missing">We don’t have a read for {sym} yet. Try another ticker.</Status>}
        {d && (
          <>
            <StockHeader d={d} following={!!follows[d.symbol]} setFollowing={(v) => setFollows({ ...follows, [d.symbol]: v })} />
            <SrTabs tab={tab} onTab={setTab} />
            <Screens d={d} tab={tab} onDetail={setDetail} onRead={read} />
            <SrFooter f={d.freshness} />
          </>
        )}
      </main>
      <SrDialog detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
